import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../config/api';
import {
  PlusIcon, MagnifyingGlassIcon, FolderIcon,
  PencilIcon, TrashIcon, ChevronRightIcon,
  ArrowLeftIcon, PlayIcon, ArrowUpTrayIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const base = API_CONFIG.BASE_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PROJECT_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b',
  '#10b981', '#06b6d4', '#f97316', '#64748b',
];

// ── Trigger badge ──────────────────────────────────────────────────────────────
function TriggerBadge({ type }) {
  const map = {
    schedule:         { label: 'Schedule',          cls: 'bg-green-100 text-green-700' },
    trigger:          { label: 'Schedule',          cls: 'bg-green-100 text-green-700' }, // legacy
    webhook:          { label: 'Webhook',           cls: 'bg-blue-100 text-blue-700' },
    chat:             { label: 'Chat',              cls: 'bg-gray-100 text-gray-600' },
    workflow_as_tool: { label: 'Workflow as Tool',  cls: 'bg-orange-100 text-orange-700' },
  };
  const { label, cls } = map[type] || map.chat;
  return <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

// ── Create / Edit project modal ────────────────────────────────────────────────
function ProjectModal({ project, onClose, onSaved }) {
  const [name, setName] = useState(project?.name || '');
  const [desc, setDesc] = useState(project?.description || '');
  const [color, setColor] = useState(project?.color || '#6366f1');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (project) {
        await axios.patch(`${base}/api/workflow-projects/${project.id}`, { name, description: desc, color }, { headers: authHeader() });
      } else {
        await axios.post(`${base}/api/workflow-projects`, { name, description: desc, color }, { headers: authHeader() });
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">{project ? 'Edit Project' : 'New Project'}</h2>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Project name <span className="text-red-500">*</span></label>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Customer Support Automations"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              autoFocus
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              value={desc} onChange={e => setDesc(e.target.value)}
              rows={2} placeholder="What is this project about?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Color</label>
            <div className="flex gap-2">
              {PROJECT_COLORS.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-transform ${color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                  style={{ background: c }} />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={handleSave} disabled={saving || !name.trim()}
            className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : project ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Project detail: workflow list inside a project ─────────────────────────────
function ProjectDetail({ project, onBack, onEditWorkflow, onRunWorkflow }) {
  const [workflows, setWorkflows] = useState([]);
  const [allWorkflows, setAllWorkflows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const importRef = useRef(null);

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!Array.isArray(data.nodes)) throw new Error('Invalid workflow JSON: missing nodes array.');
        // Open builder with no workflow_id (creates new on save), pre-filled from file
        onEditWorkflow({
          nodes:       data.nodes,
          edges:       data.edges || [],
          name:        data.name || 'Imported Workflow',
          description: data.description || '',
          project_id:  project.id === UNCATEGORIZED ? null : project.id,
        }, project.id);
      } catch (err) {
        alert('Failed to import workflow: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const openWorkflowEditor = useCallback(async (wf, projectId) => {
    try {
      const { data } = await axios.get(`${base}/api/workflows/${wf.workflow_id}`, { headers: authHeader() });
      const full = data.workflow || data;
      onEditWorkflow({ ...wf, ...full }, projectId);
    } catch {
      // Fallback to summary if fetch fails
      onEditWorkflow(wf, projectId);
    }
  }, [onEditWorkflow]);

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${base}/api/workflows`, { headers: authHeader() });
      const wfs = project.id === UNCATEGORIZED
        ? (data.workflows || []).filter(w => !w.project_id)
        : (data.workflows || []).filter(w => w.project_id === project.id);
      setAllWorkflows(wfs);
      setWorkflows(wfs);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  useEffect(() => {
    const q = search.toLowerCase();
    setWorkflows(allWorkflows.filter(w =>
      w.name.toLowerCase().includes(q) || (w.description || '').toLowerCase().includes(q)
    ));
  }, [search, allWorkflows]);

  const toggleEnabled = async (wf) => {
    await axios.patch(`${base}/api/workflows/${wf.workflow_id}/toggle-enabled`, {}, { headers: authHeader() });
    fetchWorkflows();
  };

  const deleteWorkflow = async (wf) => {
    if (!window.confirm(`Delete "${wf.name}"? This cannot be undone.`)) return;
    await axios.delete(`${base}/api/workflows/${wf.workflow_id}`, { headers: authHeader() });
    fetchWorkflows();
  };

  const downloadWorkflow = async (wf) => {
    try {
      const { data } = await axios.get(`${base}/api/workflows/${wf.workflow_id}`, { headers: authHeader() });
      const full = data.workflow || {};
      const payload = {
        name:        full.name || wf.name,
        description: full.description || wf.description || '',
        nodes:       full.nodes || [],
        edges:       full.edges || [],
        exported_at: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${(wf.name || 'workflow').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + err.message);
    }
  };

  return (
    <div className="p-6 h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: project.color + '22' }}>
          <FolderIcon className="w-5 h-5" style={{ color: project.color }} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">{project.name}</h1>
          {project.description && <p className="text-sm text-gray-500">{project.description}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <input
            ref={importRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={handleImport}
          />
          <button
            onClick={() => importRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            title="Import workflow from a JSON file"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={() => onEditWorkflow(null, project.id)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Create Workflow
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-sm">
        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search workflows..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Workflow table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>
      ) : workflows.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-4xl mb-3">🔧</div>
          <p className="text-gray-500 text-sm">{search ? 'No workflows match your search.' : 'No workflows in this project yet.'}</p>
          {!search && (
            <button
              onClick={() => onEditWorkflow(null, project.id)}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
            >
              + Create your first workflow
            </button>
          )}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: '40px' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '66px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '100px' }} />
              <col style={{ width: '148px' }} />
            </colgroup>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Nodes</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Updated</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {workflows.map((wf, idx) => (
                <tr key={wf.workflow_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <button
                        onClick={() => openWorkflowEditor(wf, project.id)}
                        title={wf.name}
                        className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline text-left leading-tight truncate min-w-0"
                      >
                        {wf.name}
                      </button>
                      <span className="flex-shrink-0"><TriggerBadge type={wf.trigger_type} /></span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 truncate overflow-hidden">{wf.description || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-indigo-100 text-indigo-600 text-xs font-bold rounded-full">{wf.node_count}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleEnabled(wf)} className="flex items-center gap-2 group">
                      <div className={`relative w-9 h-5 rounded-full transition-colors ${wf.is_enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${wf.is_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </div>
                      <span className={`text-xs ${wf.is_enabled ? 'text-green-600' : 'text-gray-400'}`}>{wf.is_enabled ? 'Enabled' : 'Disabled'}</span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {wf.updated_at ? new Date(wf.updated_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => onRunWorkflow(wf)} className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-indigo-600" title="Run">
                        <PlayIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => openWorkflowEditor(wf, project.id)} className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700" title="Edit">
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => downloadWorkflow(wf)} className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-green-600" title="Download as JSON">
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteWorkflow(wf)} className="p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-500" title="Delete">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main: project grid ─────────────────────────────────────────────────────────
// Virtual "Uncategorized" project id sentinel
const UNCATEGORIZED = '__uncategorized__';

export default function WorkflowProjects({ onEditWorkflow, onRunWorkflow, initialProjectId }) {
  const [projects, setProjects] = useState([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const restoredProjectIdRef = useRef(null);

  const UNCATEGORIZED = 'uncategorized';

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, wfRes] = await Promise.all([
        axios.get(`${base}/api/workflow-projects`, { headers: authHeader() }),
        axios.get(`${base}/api/workflows`, { headers: authHeader() }),
      ]);
      const loadedProjects = projRes.data.projects || [];
      setProjects(loadedProjects);
      const uncat = (wfRes.data.workflows || []).filter(w => !w.project_id).length;
      setUncategorizedCount(uncat);

      // Restore the active project when returning from the builder —
      // but only ONCE per initialProjectId value, not on every fetchProjects call
      // (prevents new project creation from re-navigating back into the last project)
      if (initialProjectId && restoredProjectIdRef.current !== initialProjectId) {
        restoredProjectIdRef.current = initialProjectId;
        if (initialProjectId === UNCATEGORIZED) {
          setActiveProject({ id: UNCATEGORIZED, name: 'Uncategorized', description: 'Workflows not assigned to a project', color: '#94a3b8' });
        } else {
          const found = loadedProjects.find(p => String(p.id) === String(initialProjectId));
          if (found) setActiveProject(found);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [initialProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"? Workflows inside will be unassigned.`)) return;
    await axios.delete(`${base}/api/workflow-projects/${p.id}`, { headers: authHeader() });
    fetchProjects();
  };

  if (activeProject) {
    return (
      <ProjectDetail
        project={activeProject}
        onBack={() => setActiveProject(null)}
        onEditWorkflow={(wf, projectId) => onEditWorkflow(wf, projectId)}
        onRunWorkflow={onRunWorkflow}
      />
    );
  }

  return (
    <div className="p-6 h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Workflows</h1>
          <p className="text-sm text-gray-500 mt-0.5">Organise your automations into projects</p>
        </div>
        <button
          onClick={() => { setEditingProject(null); setModalOpen(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Create Project
        </button>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20">
          <FolderIcon className="w-14 h-14 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-1">No projects yet</h3>
          <p className="text-sm text-gray-400 mb-6">Create a project to organise your workflows</p>
          <button
            onClick={() => { setEditingProject(null); setModalOpen(true); }}
            className="px-5 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
          >
            + Create your first project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* Uncategorized card — always shown if there are unassigned workflows */}
          {uncategorizedCount > 0 && (
            <div
              className="bg-white border border-dashed border-gray-300 rounded-2xl p-5 hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => setActiveProject({ id: UNCATEGORIZED, name: 'Uncategorized', description: 'Workflows not assigned to a project', color: '#94a3b8' })}
            >
              <div className="w-full h-1 rounded-full mb-4 bg-gray-300" />
              <div className="flex items-start justify-between mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gray-100">
                  <FolderIcon className="w-5 h-5 text-gray-400" />
                </div>
              </div>
              <h3 className="font-semibold text-gray-500 text-sm mb-1">Uncategorized</h3>
              <p className="text-xs text-gray-400 mb-3">Workflows not assigned to a project</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-gray-400">{uncategorizedCount} workflow{uncategorizedCount !== 1 ? 's' : ''}</span>
                <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-indigo-400 transition-colors" />
              </div>
            </div>
          )}
          {projects.map(p => (
            <div
              key={p.id}
              className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow cursor-pointer group relative"
              onClick={() => setActiveProject(p)}
            >
              {/* Color bar */}
              <div className="w-full h-1 rounded-full mb-4" style={{ background: p.color }} />

              {/* Icon + name */}
              <div className="flex items-start justify-between mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: p.color + '20' }}>
                  <FolderIcon className="w-5 h-5" style={{ color: p.color }} />
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={e => { e.stopPropagation(); setEditingProject(p); setModalOpen(true); }}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                  >
                    <PencilIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); handleDelete(p); }}
                    className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <h3 className="font-semibold text-gray-800 text-sm mb-1 truncate">{p.name}</h3>
              {p.description && <p className="text-xs text-gray-400 mb-3 line-clamp-2">{p.description}</p>}

              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-gray-400">
                  {p.workflow_count} workflow{p.workflow_count !== 1 ? 's' : ''}
                </span>
                <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-indigo-400 transition-colors" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <ProjectModal
          project={editingProject}
          onClose={() => { setModalOpen(false); setEditingProject(null); }}
          onSaved={() => { setModalOpen(false); setEditingProject(null); fetchProjects(); }}
        />
      )}
    </div>
  );
}
